import { classNames, useGlobalId, useUpdateRef } from '@zambezi/sdk/common'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useRef, useState } from 'react'
import List from './List'

/**
 * Create a React Dropdown List component
 *
 * @template T
 * @template Item
 * @param {import('./interfaces').DropdownListProps<T, Item>} props
 * @param {React.Ref<HTMLElement>} ref
 * @returns {JSX.Element}
 */
function dropdownList (
  {
    data,
    header,
    footer,
    className,
    arrowOffset,
    arrowPosition,
    id,
    withGrouping,
    renderItem,
    renderGroupLabel,
    getGroupChildren,
    itemProps,
    itemClassName,
    disabledItemsIndices,
    onGroupLabelClick,
    onItemClick,
    listProps,
    withSpaceSelect
  },
  ref
) {
  const [focusedItemIndex, setFocusedItemIndex] = useState(-1)
  const dropdownListId = useGlobalId('zb-dropdownlist-')
  /** @type {React.RefObject<HTMLElement>} */
  const listElementRef = useRef(null)
  /** @type {React.RefObject<HTMLElement>} */
  const dropdownElementRef = useRef(null)

  const headerId = `${dropdownListId}-header`
  /** @type {(index: number) => boolean} */
  const isItemDisabled = useCallback(
    index => disabledItemsIndices.indexOf(index) !== -1,
    [disabledItemsIndices]
  )
  const onMouseOut = useCallback(() => setFocusedItemIndex(-1), [
    setFocusedItemIndex
  ])
  /** @type {import('./interfaces').ItemMouseEventHandler<Item>} */
  const onMouseOver = useCallback(
    (data, index, event) => setFocusedItemIndex(index),
    [setFocusedItemIndex]
  )

  useUpdateRef(ref, dropdownElementRef, { focus })

  return (
    <div
      className={classNames(
        'zb-dropdown-container',
        {
          'zb-dropdown-container-arrow-left': arrowPosition === 'left',
          'zb-dropdown-container-arrow-right': arrowPosition === 'right'
        },
        className
      )}
      tabIndex='-1'
      ref={dropdownElementRef}
      onKeyDown={handleKeyPress}
      id={id}
    >
      <span
        className='zb-dropdown-arrow'
        style={
          arrowPosition === 'left'
            ? { left: arrowOffset }
            : { right: arrowOffset }
        }
      />

      <section className='zb-dropdown-header' id={headerId}>
        {header}
      </section>
      <List
        ref={listElementRef}
        aria-labelledby={headerId}
        tabIndex='0'
        {...{
          data,
          dropdownElementRef,
          dropdownListId,
          focusedItemIndex,
          getGroupChildren,
          isItemDisabled,
          itemClassName,
          itemProps,
          onGroupLabelClick,
          onItemClick,
          onMouseOut,
          onMouseOver,
          renderGroupLabel,
          renderItem,
          withGrouping
        }}
        {...listProps}
      />

      {footer && <section className='zb-dropdown-footer'>{footer}</section>}
    </div>
  )

  /**
   * @param {FocusOptions|number} focusOptions
   */
  function focus (focusOptions) {
    const dropdownElement = listElementRef.current
    if (!isNaN(focusOptions) && !isItemDisabled(focusOptions)) {
      setFocusedItemIndex(focusOptions)
    }
    if (dropdownElement !== document.activeElement) {
      dropdownElement.focus(focusOptions)
    }
  }

  /**
   * @param {React.KeyboardEvent} event
   */
  function handleKeyPress (event) {
    /** @type {Item[]} */
    const flattenedData = withGrouping
      ? data.reduce(
        /** @type {(groups: Item[], group: T) => Item[]} */
        (groups, children, index) =>
          groups.concat(getGroupChildren(children, index)),
        []
      )
      : data
    const numberOfItems = flattenedData.length
    switch (event.key) {
      case 'ArrowDown':
        setFocusedItemIndex(
          nextNot(focusedItemIndex, isItemDisabled, numberOfItems - 1)
        )
        event.preventDefault()
        break
      case 'ArrowUp':
        setFocusedItemIndex(prevNot(focusedItemIndex, isItemDisabled, 0))
        event.preventDefault()
        break
      case 'Home':
        setFocusedItemIndex(nextNot(-1, isItemDisabled, numberOfItems - 1))
        event.preventDefault()
        break
      case 'End':
        setFocusedItemIndex(prevNot(numberOfItems, isItemDisabled, 0))
        event.preventDefault()
        break
      case 'Spacebar': // falls through
      case ' ':
        if (withSpaceSelect) event.preventDefault()
        else break
      // falls through
      case 'Enter':
        onItemClick(flattenedData[focusedItemIndex], focusedItemIndex, event)
        break
    }
  }
}

function noop () {}

/**
 * @template T
 * @template Item
 * @type {React.ForwardRefExoticComponent<import('./interfaces').DropdownListProps<T, Item> & React.RefAttributes<HTMLElement>>}
 */
const DropdownList = forwardRef(dropdownList)
DropdownList.displayName = 'DropdownList'

DropdownList.propTypes = {
  data: PropTypes.array,
  disabledItemsIndices: PropTypes.array,
  renderItem: PropTypes.func,
  header: PropTypes.element,
  footer: PropTypes.element,
  arrowPosition: PropTypes.oneOf(['left', 'right']),
  arrowOffset: PropTypes.string,
  itemClassName: PropTypes.func,
  itemProps: PropTypes.func,
  onItemClick: PropTypes.func,
  withSpaceSelect: PropTypes.bool,
  withGrouping: PropTypes.bool,
  renderGroupLabel: PropTypes.func,
  getGroupChildren: PropTypes.func,
  onGroupLabelClick: PropTypes.func
}

DropdownList.defaultProps = {
  data: [],
  disabledItemsIndices: [],
  renderItem: String,
  header: null,
  footer: null,
  arrowOffset: '8px',
  arrowPosition: 'right',
  itemClassName: noop,
  itemProps: noop,
  onItemClick: noop,
  withSpaceSelect: false,
  withGrouping: false,
  renderGroupLabel: String,
  getGroupChildren: () => [],
  onGroupLabelClick: noop
}

export default DropdownList

/**
 * @param {number} from
 * @param {(index: number) => boolean} predicate
 * @param {number} max
 */
function nextNot (from, predicate, max) {
  for (let newValue = Math.min(from + 1, max); newValue <= max; newValue++) {
    if (!predicate(newValue)) return newValue
  }

  return from
}

/**
 * @param {number} from
 * @param {(index: number) => boolean} predicate
 * @param {number} max
 */
function prevNot (from, predicate, min) {
  for (let newValue = Math.max(from - 1, min); newValue >= min; newValue--) {
    if (!predicate(newValue)) return newValue
  }

  return from
}
